/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/no-children-prop */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from '@tvg/fuse/link';
import { IconDescription } from '@tvg/fuse/icon/all';
import { AccordionGroup } from '@tvg/fuse/accordion-group';
import loadOneWorldSync from 'utilities/oneWorldSync/oneWorldSync-util';
import { OneWorldSyncLoaderInfoProps, OneWorldSyncInlineContentProps } from 'types/OneWorldSyncLoaderTypes';
import ThirdPartyExtensions from 'components/ThirdPartyExtensions/ThirdPartyExtensions';
import {
  AccordionWrapperDiv,
  Container,
  FeaturesContainer,
  LinksContainer,
  StyledAccordion,
  TechSpecsContainer,
  DocsContainer,
} from './InlineContentStyles';
import accordionsProps from './InlineContentProps';

const { descriptionProps, featuresProps, techSpecsProps, documentationProps } = accordionsProps;

interface DocLink {
  href: string;
  text: string;
}

function InlineContent(props: OneWorldSyncInlineContentProps) {
  const { brand, mpn, ean, option, Description, AppleContent, LoadBee, flixMediaEnabled } = props;
  const [featuresHTML, setFeaturesHTML] = useState('');
  const [specsHTML, setSpecsHTML] = useState('');
  const [docsHTML, setDocsHTML] = useState('');
  const [docLinks, setDocLinks] = useState<DocLink[]>([]);
  const captured = useRef({ features: false, specs: false, docs: false });

  useEffect(() => {
    if (brand) {
      const loaderInfo: OneWorldSyncLoaderInfoProps = {
        brand,
        window,
        option,
        mpn,
        ean,
      };
      loadOneWorldSync(loaderInfo);
    }
  }, [brand]);

  useEffect(() => {
    const interval = setInterval(() => {
      const feats = document.querySelector<HTMLElement>('[data-testid="oneworldsync-features"]');
      const specs = document.querySelector<HTMLElement>('[data-testid="oneworldsync-combo-spec"]');
      const docs = document.querySelector<HTMLElement>('[data-testid="oneworldsync-documents"]');

      if (feats && specs && docs) {
        if (feats.hasChildNodes() && !captured.current.features) {
          captured.current.features = true;
          setFeaturesHTML(feats.innerHTML);
        }
        if (specs.hasChildNodes() && !captured.current.specs) {
          captured.current.specs = true;
          setSpecsHTML(specs.innerHTML);
        }
        if (docs.hasChildNodes() && !captured.current.docs) {
          captured.current.docs = true;
          setDocsHTML(docs.innerHTML);
          setDocLinks(
            Array.from(docs.children)
              .map((el) => ({
                href: (el.children[0] as HTMLAnchorElement)?.href ?? '',
                text: (el.children[0] as HTMLElement)?.innerText ?? '',
              }))
              .filter((link): link is DocLink => !!link.href),
          );
        }
      }
    }, 1000);

    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  return Description ? (
    <AccordionWrapperDiv>
      {/* Hidden injection targets for OneWorldSync 3rd party script */}
      <div hidden>
        <div id="ccs-features" data-testid="oneworldsync-features" />
        <div id="ccs-combo-spec" data-testid="oneworldsync-combo-spec" />
        <div id="ccs-documents" data-testid="oneworldsync-documents" />
      </div>
      <AccordionGroup>
        <StyledAccordion
          {...descriptionProps}
          open
          children={
            <>
              {Description}
              {AppleContent && AppleContent}
              {LoadBee && LoadBee}
              {flixMediaEnabled ? (
                <ThirdPartyExtensions />
              ) : (
                <Container id="ccs-inline-content" data-testid="oneworldsync-inline-content" />
              )}
            </>
          }
        />
        {featuresHTML && (
          <StyledAccordion
            {...featuresProps}
            children={<FeaturesContainer dangerouslySetInnerHTML={{ __html: featuresHTML }} />}
          />
        )}
        {specsHTML && (
          <StyledAccordion
            {...techSpecsProps}
            children={<TechSpecsContainer dangerouslySetInnerHTML={{ __html: specsHTML }} />}
          />
        )}
        {docsHTML && (
          <StyledAccordion
            {...documentationProps}
            children={
              <>
                <DocsContainer dangerouslySetInnerHTML={{ __html: docsHTML }} />
                {docLinks.length > 0 && (
                  <LinksContainer data-testid="oneworldsync-custom-documents">
                    {docLinks.map((link, i) => (
                      <Link
                        dataTestId={`docs-link-${i + 1}`}
                        href={link.href}
                        disabled={false}
                        standalone
                        icon={<IconDescription />}
                        target="_blank"
                      >
                        View {link.text.toLowerCase()}
                      </Link>
                    ))}
                  </LinksContainer>
                )}
              </>
            }
          />
        )}
      </AccordionGroup>
    </AccordionWrapperDiv>
  ) : flixMediaEnabled ? (
    <ThirdPartyExtensions />
  ) : (
    <Container id="ccs-inline-content" data-testid="oneworldsync-inline-content" />
  );
}

export default InlineContent;
